import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { StatusLabelComponent } from '@principle-theorem/ng-principle-shared';
import {
  ObservableDataSource,
  PipesModule,
  extendSortingDataAccessor,
  timestampSortingAccessor,
} from '@principle-theorem/ng-shared';
import { PRESCRIPTION_STATUS_COLOUR_MAP } from '@principle-theorem/principle-core';
import {
  IPatientPrescriptionSummary,
  PrescriptionProvider,
  PrescriptionStatus,
} from '@principle-theorem/principle-core/interfaces';
import { HISTORY_DATE_FORMAT, Reffable } from '@principle-theorem/shared';
import { MomentModule } from 'ngx-moment';
import { ReplaySubject, Subject } from 'rxjs';

type PrescriptionSummary =
  | IPatientPrescriptionSummary
  | Reffable<IPatientPrescriptionSummary>;

@Component({
  selector: 'pr-patient-prescriptions-list',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    PipesModule,
    MomentModule,
    StatusLabelComponent,
  ],
  templateUrl: './patient-prescriptions-list.component.html',
  styleUrl: './patient-prescriptions-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientPrescriptionsListComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _prescriptions$ = new ReplaySubject<PrescriptionSummary[]>(1);
  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly principleProvider = PrescriptionProvider.Principle;
  actionLabel = input<string>('View Prescription');
  dataSource: ObservableDataSource<PrescriptionSummary>;
  displayedColumns: string[] = [
    'createdAt',
    'prescriber',
    'items',
    'status',
    'actions',
  ];

  @Output() prescriptionSelected = new EventEmitter<PrescriptionSummary>();

  @Input()
  set prescriptions(prescriptions: PrescriptionSummary[]) {
    if (prescriptions) {
      this._prescriptions$.next(prescriptions);
    }
  }

  @ViewChild(MatPaginator)
  set tablePaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  constructor() {
    this.dataSource = new ObservableDataSource(this._prescriptions$);
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  statusColor(status: PrescriptionStatus): string {
    return PRESCRIPTION_STATUS_COLOUR_MAP[status];
  }

  private _sortingDataAccessor(
    data: PrescriptionSummary,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'createdAt':
        return timestampSortingAccessor(data.createdAt);
      default:
        return;
    }
  }
}
